/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import {getSetting, IndexedObject, navigate, ResponseData, showDialog} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {useContext, useEffect, useRef, useState} from 'react';
import Subscription from '../../../models/subscriptionModel';
import SubscriptionSettings from './subscriptionSettings';
import {getSubscriptionUsage} from '../../../util/requestSender';
import {createErrorMessage} from '../../../util/utils';
import {loadStripePortal, startCheckout} from '../../../util/subscriptionUtil';
import {ActivateHandle, CancelType, DiscountType} from '../../../util/interfaces';
import PaymentMethod from './paymentMethod';
import {onSetCrumbs} from '../../../util/events';
import CancelSubscriptionController from './cancelSubscription/cancelSubscriptionController';
import SetVatNumberController from './setVatNumber/setVatNumberController';
import {AppContext} from '../../misc/appContext';
import {paymentInfoEvent} from '../../../util/analytics';
import SubscriptionCouponController from "./coupon/subscriptionCouponController";
import CancelDiscountOfferController from "./cancelDiscountOffer/cancelDiscountOfferController";
import {fallbackUsersEstimate, getActiveUsersEstimate} from "../../../util/statUtil";
import CustomerDetailsController from "./customerDetails/customerDetailsController";

export type SubscriptionModuleProps = {
    loading: boolean;
    usageLastMonth: number;
}

export default function SubscriptionSettingsController() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account} = context.accountContext;
    const [currentUsage, setCurrentUsage] = useState(0);
    const [currentPrice, setCurrentPrice] = useState(0);
    const [discountPrice, setDiscountPrice] = useState<number | null>(null);
    const [periodFrom, setPeriodFrom] = useState<IDate | null>(null);
    const [periodTo, setPeriodTo] = useState<IDate | null>(null);
    const [subscription, setSubscription] = useState(user?.subscription ?? new Subscription());
    const [loading, setLoading] = useState(false);
    const [prevMonths, setPrevMonths] = useState<IndexedObject<number>>({});
    const [lastUsage, setLastUsage] = useState(0);
    const [extraAccounts, setExtraAccounts] = useState(0);
    const [paymentActive, setPaymentActive] = useState(false);
    const [loadingPayment, setLoadingPayment] = useState(false);
    const [usersEstimate, setUsersEstimate] = useState(fallbackUsersEstimate);
    const cancelRef = useRef<ActivateHandle>(null);
    const vatNumberRef = useRef<ActivateHandle>(null);
    const couponRef = useRef<ActivateHandle>(null);
    const cancelDiscountRef = useRef<ActivateHandle>(null);
    const customerDetailsRef = useRef<ActivateHandle>(null);

    function onOpenCustomerDetails() {
        customerDetailsRef.current?.activate();
    }

    function onClickPortal() {
        if (!account) return;
        setLoading(true);
        const returnUrl = getSetting('appUrl') + '/instellingen/abonnement';
        loadStripePortal(returnUrl, account.id).then((url: string) => {
            setLoading(false);
            window.location.href = url;
        }).catch((message?: string) => {
            setLoading(false);
            showDialog(createErrorMessage(message ?? 'Er is iets mis gegaan.'), 'Mislukt');
        });
    }

    function onActivatePayment(_subscription?: Subscription) {
        return function() {
            if (!_subscription) _subscription = subscription;
            if (!_subscription.vatNumber) return vatNumberRef.current?.activate();
            setPaymentActive(true);
            paymentInfoEvent();
        }
    }

    function onClosePayment() {
        setPaymentActive(false);
        setLoadingPayment(false);
    }

    function onChoosePaymentMethod(paymentMethod: 'ideal' | 'cc' | 'sepa') {
        return function() {
            if (paymentMethod === 'ideal') navigate('/ideal');
            else onStartCheckout(paymentMethod);
        }
    }

    function onStartCheckout(paymentMethod: 'cc' | 'sepa') {
        if (!account) return;
        setLoadingPayment(true);
        const appUrl = getSetting('appUrl'),
            returnUrl = appUrl + '/facturen?subscription=1',
            cancelUrl = appUrl + '/instellingen/abonnement',
            method = paymentMethod === 'cc' ? 'card' : 'sepa_debit';
        startCheckout(account.id, returnUrl, cancelUrl, undefined, method);
    }

    function onOfferCancelDiscount() {
        if (context.userContext.user?.subscription?.discount) return onCancelSubscription();
        cancelDiscountRef.current?.activate();
    }

    function onOfferDiscountSuccess(subscription: Subscription) {
        setSubscription(subscription);
    }

    function onCancelSubscription() {
        if (cancelRef.current) {
            cancelRef.current.activate();
        } else onClickPortal();
    }

    function onCancelSuccess() {
        window.location.reload();
    }

    function onVatNumberSuccess(subscription: Subscription) {
        const {user} = context.userContext;
        if (user) {
            user.subscription = subscription;
            context.onChangeContext({user});
        }
        onActivatePayment(subscription)();
    }

    function onClickCoupon() {
        couponRef.current?.activate();
    }

    function onClickFailedPayment() {
        const failedPayment = context.userContext.user?.subscription?.failedPayment;
        if (failedPayment?.paymentUrl) window.open(failedPayment.paymentUrl, '_blank');
        else showDialog('Er is nog geen nieuwe factuur gemaakt voor de mislukte betaling, neem contact met ons op om ' +
            'het abonnement te hervatten.', 'Openstaande factuur');
    }

    function loadSubscription(callback?: (subscription: Subscription) => void) {
        if (!account) return;
        if (!loading) setLoading(true);
        getSubscriptionUsage({
            account_id: account.id,
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    const {
                        current_usage, current_price, period_start, period_end, previous_periods, subscription,
                        total_usage_last_month, extra_accounts
                    } = data;
                    setCurrentUsage(current_usage);
                    setCurrentPrice(current_price);
                    setPeriodTo(new IDate(period_end));
                    setPeriodFrom(new IDate(period_start));
                    setPrevMonths(previous_periods);
                    setLastUsage(total_usage_last_month);
                    setExtraAccounts(extra_accounts);
                    const sub = new Subscription(subscription);
                    setSubscription(sub);
                    callback?.(sub);
                } else showDialog(createErrorMessage(data.message ?? 'Het laden van je abonnement is niet gelukt.'),
                    'Laden mislukt');
            }
        });
    }

    function loadUsersEstimate() {
        getActiveUsersEstimate().then((estimate: number) => {
            setUsersEstimate(estimate);
        });
    }

    function calculateDiscountPrice() {
        const {discountType, discount} = subscription;
        if (!discount || !discountType) return;
        let discountPrice: number;
        if (discountType === DiscountType.PERCENT)
            discountPrice = currentPrice * ((100 - discount) / 100);
        else
            discountPrice = Math.max(0, currentPrice - discount);
        setDiscountPrice(discountPrice);
    }

    useEffect(() => {
        onSetCrumbs([{label: 'Instellingen'}, {label: 'Abonnement'}]);
        loadSubscription();
        loadUsersEstimate();
    }, []);

    useEffect(() => {
        calculateDiscountPrice();
    }, [subscription, currentPrice]);

    return (
        <>
            <SubscriptionSettings onClickPortal={onClickPortal} onClickStart={onActivatePayment()}
                                  onCancelSubscription={onOfferCancelDiscount} onClickCoupon={onClickCoupon}
                                  onClickFailedPayment={onClickFailedPayment}
                                  onOpenCustomerDetails={onOpenCustomerDetails} currentUsage={currentUsage}
                                  currentPrice={currentPrice} usageLastMonth={lastUsage} currentPeriodFrom={periodFrom}
                                  currentPeriodTo={periodTo} subscription={subscription} loadingSubscription={loading}
                                  previousPeriods={prevMonths} discountPrice={discountPrice}
                                  extraAccounts={extraAccounts} activeUsersEstimate={usersEstimate}/>
            <PaymentMethod onChoose={onChoosePaymentMethod} onClose={onClosePayment}
                           active={paymentActive} loading={loadingPayment} activeUsersEstimate={usersEstimate}/>
            <CancelSubscriptionController ref={cancelRef} type={CancelType.SUBSCRIPTION} onSuccess={onCancelSuccess}/>
            <SetVatNumberController ref={vatNumberRef} onSuccess={onVatNumberSuccess}/>
            <SubscriptionCouponController ref={couponRef} onSuccess={loadSubscription}/>
            <CancelDiscountOfferController ref={cancelDiscountRef} onProceed={onCancelSubscription}
                                           onSuccess={onOfferDiscountSuccess}/>
            <CustomerDetailsController ref={customerDetailsRef}/>
        </>
    )
}